import postFetcher from 'api/utils/postFetcher';
import { useCallback, useMemo } from 'react';
import type {
  HandlersSimsResponse,
  StructsSimRequestProperties,
} from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap } from 'types/common';

interface UseSimsByIdCallProps {
  sims?: HologramSimIdsMap;
  organizationId?: OrgId;
  isSuperAdmin: boolean;
}

const MAX_CHUNK = 10000 as const;
const MAX_REQUESTS = 5 as const;

async function* resultGenerator(
  url: string,
  organizationId: OrgId,
  isSuperAdmin: boolean,
  requestChunks: Array<Array<Array<HologramSimId>>>
) {
  let i = 0;

  while (i < requestChunks.length) {
    const requests = requestChunks[i].map(async (chunk) => {
      const body: StructsSimRequestProperties = {
        pagination: { disable: true },
        hologramSimIds: chunk,
      };

      if (!isSuperAdmin) {
        body.organizationId = organizationId;
      }

      const result: HandlersSimsResponse = await postFetcher({
        url,
        body,
      });

      return result;
    });

    yield Promise.all(requests);
    i += 1;
  }
}

const useSimsByIdCall = ({ sims, organizationId, isSuperAdmin }: UseSimsByIdCallProps) => {
  const url =
    sims && (organizationId || isSuperAdmin)
      ? `${process.env.VITE_BIZ_API_ROOT}/sim-inventory/sims`
      : null;

  const requestChunks = useMemo(() => {
    const chunks = [...sims!.keys()].reduce<Array<Array<HologramSimId>>>(
      (resultArray, item, index) => {
        const chunkIndex = Math.floor(index / MAX_CHUNK);

        if (!resultArray[chunkIndex]) {
          // eslint-disable-next-line no-param-reassign
          resultArray[chunkIndex] = [];
        }

        resultArray[chunkIndex].push(item);

        return resultArray;
      },
      []
    );

    return chunks.reduce<Array<Array<Array<HologramSimId>>>>((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / MAX_REQUESTS);

      if (!resultArray[chunkIndex]) {
        // eslint-disable-next-line no-param-reassign
        resultArray[chunkIndex] = [];
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);
  }, [sims]);

  const callback = useCallback(async () => {
    if (!url || !organizationId) {
      return undefined;
    }

    let results: Array<HandlersSimsResponse> = [];
    // eslint-disable-next-line no-restricted-syntax
    for await (const resultChunk of resultGenerator(
      url,
      organizationId,
      isSuperAdmin,
      requestChunks
    )) {
      results = results.concat(resultChunk);
    }

    return results;
  }, [isSuperAdmin, organizationId, requestChunks, url]);

  return callback;
};

export default useSimsByIdCall;
